import {
  COL_FINISHED_GOODS_LOTS,
  COL_PRODUCTION_REQUEST_LINES,
  COL_PRODUCTION_REQUESTS,
  COL_SCHEDULE_SLOTS,
  COL_SHIPMENT_LINES,
  COL_SLOT_MATERIAL_USAGE,
  COL_TIMESHEET_ENTRIES,
} from '../config/collections'
import { pb } from '../pocketbase'

function orFilter(field: string, ids: string[]): string {
  return ids.map((id) => `${field}="${id}"`).join('||')
}

export type ProductionRequestDeleteImpact = {
  lines: number
  scheduleSlots: number
  shipmentLines: number
  materialUsage: number
  timesheetUnlinked: number
  finishedGoodsUnlinked: number
}

async function gatherLineDeleteTargets(lineIds: string[]) {
  const uniqueLineIds = [...new Set(lineIds.filter(Boolean))]
  if (uniqueLineIds.length === 0) {
    return {
      uniqueLineIds,
      allSlotIds: [] as string[],
      shipLineIds: [] as string[],
      materialUsage: 0,
      timesheetUnlinked: 0,
      finishedGoodsUnlinked: 0,
    }
  }

  const lineFilter = orFilter('requestLine', uniqueLineIds)
  const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<{ id: string }>({
    filter: lineFilter,
  })

  const slotIds = new Set(slots.map((s) => s.id))
  if (slotIds.size > 0) {
    let added = true
    while (added) {
      added = false
      const reworkFilter = [...slotIds].map((id) => `reworkOf="${id}"`).join('||')
      const reworkSlots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<{ id: string }>({
        filter: reworkFilter,
      })
      for (const s of reworkSlots) {
        if (!slotIds.has(s.id)) {
          slotIds.add(s.id)
          added = true
        }
      }
    }
  }

  const allSlotIds = [...slotIds]
  let materialUsage = 0
  let timesheetUnlinked = 0
  let finishedGoodsUnlinked = 0

  if (allSlotIds.length > 0) {
    const slotFilter = orFilter('slot', allSlotIds)
    const scheduleSlotFilter = orFilter('scheduleSlot', allSlotIds)
    const [usages, tsEntries, lots] = await Promise.all([
      pb.collection(COL_SLOT_MATERIAL_USAGE).getFullList<{ id: string }>({ filter: slotFilter }),
      pb.collection(COL_TIMESHEET_ENTRIES).getFullList<{ id: string }>({ filter: scheduleSlotFilter }),
      pb.collection(COL_FINISHED_GOODS_LOTS).getFullList<{ id: string }>({ filter: scheduleSlotFilter }),
    ])
    materialUsage = usages.length
    timesheetUnlinked = tsEntries.length
    finishedGoodsUnlinked = lots.length
  }

  const shipLines = await pb.collection(COL_SHIPMENT_LINES).getFullList<{ id: string }>({
    filter: lineFilter,
  })

  return {
    uniqueLineIds,
    allSlotIds,
    shipLineIds: shipLines.map((sl) => sl.id),
    materialUsage,
    timesheetUnlinked,
    finishedGoodsUnlinked,
  }
}

/** Считает, что затронет удаление позиций заявки. */
export async function getLineDeleteImpact(lineIds: string[]): Promise<ProductionRequestDeleteImpact> {
  const t = await gatherLineDeleteTargets(lineIds)
  return {
    lines: t.uniqueLineIds.length,
    scheduleSlots: t.allSlotIds.length,
    shipmentLines: t.shipLineIds.length,
    materialUsage: t.materialUsage,
    timesheetUnlinked: t.timesheetUnlinked,
    finishedGoodsUnlinked: t.finishedGoodsUnlinked,
  }
}

/** Считает, что затронет удаление всей заявки. */
export async function getProductionRequestDeleteImpact(
  requestId: string,
): Promise<ProductionRequestDeleteImpact> {
  const lines = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<{ id: string }>({
    filter: `request="${requestId}"`,
  })
  return getLineDeleteImpact(lines.map((l) => l.id))
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

/** Текст подтверждения удаления с перечислением затронутых данных. */
export function formatDeleteImpactConfirm(
  impact: ProductionRequestDeleteImpact,
  kind: 'request' | 'lines',
): string {
  const header =
    kind === 'request'
      ? 'Удалить заявку на производство?'
      : impact.lines === 1
        ? 'Удалить позицию заявки?'
        : `Удалить ${impact.lines} поз. заявки?`

  const deleted: string[] = []
  if (kind === 'request' && impact.lines > 0) {
    deleted.push(
      `• ${impact.lines} ${plural(impact.lines, 'позицию', 'позиции', 'позиций')} заявки`,
    )
  } else if (kind === 'lines' && impact.lines > 1) {
    deleted.push(`• ${impact.lines} поз. заявки`)
  }
  if (impact.scheduleSlots > 0) {
    deleted.push(
      `• ${impact.scheduleSlots} ${plural(impact.scheduleSlots, 'слот', 'слота', 'слотов')} графика производства`,
    )
  }
  if (impact.shipmentLines > 0) {
    deleted.push(
      `• ${impact.shipmentLines} ${plural(impact.shipmentLines, 'строку', 'строки', 'строк')} в отгрузках`,
    )
  }
  if (impact.materialUsage > 0) {
    deleted.push(
      `• ${impact.materialUsage} ${plural(impact.materialUsage, 'запись', 'записи', 'записей')} расхода сырья по слотам`,
    )
  }

  const unlinked: string[] = []
  if (impact.timesheetUnlinked > 0) {
    unlinked.push(
      `• ${impact.timesheetUnlinked} ${plural(impact.timesheetUnlinked, 'запись', 'записи', 'записей')} табеля (останутся, слот будет снят)`,
    )
  }
  if (impact.finishedGoodsUnlinked > 0) {
    unlinked.push(
      `• ${impact.finishedGoodsUnlinked} ${plural(impact.finishedGoodsUnlinked, 'партию', 'партии', 'партий')} готовой продукции на складе (останутся, слот будет снят)`,
    )
  }

  if (deleted.length === 0 && unlinked.length === 0) {
    return kind === 'request'
      ? `${header}\n\nБудут удалены заявка и все её позиции. Связанных данных в графике и отгрузках нет.`
      : `${header}\n\nСвязанных данных в графике и отгрузках нет.`
  }

  const parts = [header, '']
  if (deleted.length > 0) {
    parts.push('Будет удалено без возможности восстановления:', ...deleted)
  }
  if (unlinked.length > 0) {
    if (deleted.length > 0) parts.push('')
    parts.push('Будет отвязано (сами записи сохранятся):', ...unlinked)
  }
  parts.push('', 'Продолжить?')
  return parts.join('\n')
}

/** Снимает связи и удаляет данные, мешающие удалить строки заявки. */
export async function cleanupBeforeProductionRequestLineDelete(lineIds: string[]): Promise<void> {
  const t = await gatherLineDeleteTargets(lineIds)
  if (t.uniqueLineIds.length === 0) return

  if (t.allSlotIds.length > 0) {
    const slotFilter = orFilter('slot', t.allSlotIds)
    const scheduleSlotFilter = orFilter('scheduleSlot', t.allSlotIds)

    const [usages, tsEntries, lots] = await Promise.all([
      pb.collection(COL_SLOT_MATERIAL_USAGE).getFullList<{ id: string }>({ filter: slotFilter }),
      pb.collection(COL_TIMESHEET_ENTRIES).getFullList<{ id: string }>({ filter: scheduleSlotFilter }),
      pb.collection(COL_FINISHED_GOODS_LOTS).getFullList<{ id: string }>({ filter: scheduleSlotFilter }),
    ])

    await Promise.all([
      ...usages.map((u) => pb.collection(COL_SLOT_MATERIAL_USAGE).delete(u.id)),
      ...tsEntries.map((e) =>
        pb.collection(COL_TIMESHEET_ENTRIES).update(e.id, { scheduleSlot: null }),
      ),
      ...lots.map((l) => pb.collection(COL_FINISHED_GOODS_LOTS).update(l.id, { scheduleSlot: null })),
      ...t.allSlotIds.map((id) => pb.collection(COL_SCHEDULE_SLOTS).update(id, { reworkOf: null })),
    ])

    await Promise.all(t.allSlotIds.map((id) => pb.collection(COL_SCHEDULE_SLOTS).delete(id)))
  }

  await Promise.all(t.shipLineIds.map((id) => pb.collection(COL_SHIPMENT_LINES).delete(id)))
}

/** Удаляет заявку на производство вместе с позициями и связанными слотами графика. */
export async function deleteProductionRequest(requestId: string): Promise<void> {
  const lines = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<{ id: string }>({
    filter: `request="${requestId}"`,
  })
  await cleanupBeforeProductionRequestLineDelete(lines.map((l) => l.id))
  await pb.collection(COL_PRODUCTION_REQUESTS).delete(requestId)
}

/** Удаляет заявку после подтверждения с перечислением затронутых данных. */
export async function deleteProductionRequestWithConfirm(requestId: string): Promise<boolean> {
  const impact = await getProductionRequestDeleteImpact(requestId)
  if (!window.confirm(formatDeleteImpactConfirm(impact, 'request'))) return false
  await deleteProductionRequest(requestId)
  return true
}

/** Удаляет позиции заявки после подтверждения (если есть связанные данные). */
export async function confirmLineDeleteImpact(lineIds: string[]): Promise<boolean> {
  const impact = await getLineDeleteImpact(lineIds)
  const hasRelated =
    impact.scheduleSlots > 0 ||
    impact.shipmentLines > 0 ||
    impact.materialUsage > 0 ||
    impact.timesheetUnlinked > 0 ||
    impact.finishedGoodsUnlinked > 0
  if (!hasRelated) return true
  return window.confirm(formatDeleteImpactConfirm(impact, 'lines'))
}
